import { Observable } from 'rxjs';
import { Sizer } from '../../sizer.class';
import { Dragger } from '../../dragger.class';
import { Viewport } from '../../../../utils/viewport';
import { Selection, SelectionArgs, stubSelection } from '../../selection.class';
import { Cols } from '../../cols/cols.class';
import { Rows } from '../../rows.class';
import { Dom } from '../../dom.class';
import { Chain } from '../../chain.class';
import { AbstractSorter } from './sorter.class';
import { AbstractMenu } from './menu/menu.component';
import { BodyCell } from '../../../cells/holders/body-cell.component';
import { HeaderCell } from '../../../cells/holders/header-cell.component';
import { RemoteCol } from '../remote/table.class';
import { LocalCol } from '../local/table.class';


export interface IAbstractCol<D extends object> {
   minWidth: number;
   cell: BodyCell<D>;
   title?: () => string;
   isVisible?: boolean;
   isPinned?: boolean;
   header?: HeaderCell;
}


export interface ICol<D extends object> extends RemoteCol<D>, LocalCol<D> {
   id?: string;
   isControlable?: boolean;
}


export interface ColsArgs<D extends object> {
   templates: ColsMap<D>,
   isDragable?: boolean,
   isSizable?: boolean,
}


export interface ColsMap<D extends object> {
   [id: string]: ICol<D>;
}


export interface RowsArgs<D extends object> {
   source: Observable<D[]>;
   maxVisibleCount?: number;
};


export interface TableArgs<D extends object> {
   id: string;
   rows: RowsArgs<D>;
   cols: ColsArgs<D>;
   isCashable?: boolean;
   selection?: SelectionArgs<D>,
}


export abstract class AbstractTable<D extends object> {
   readonly id: string;
   readonly isCashable: boolean; 
   readonly dom: Dom;
   readonly cols: Cols<D>;
   readonly rows: Rows<D>;
   readonly sizer: Sizer<D>;
   readonly dragger: Dragger<D>;
   readonly selection: Selection<D>;
   readonly viewport: Viewport<D>;
   readonly source: Observable<D[]>;
   abstract chain: Chain<D>
   abstract sorter: AbstractSorter<D>;
   abstract menu: AbstractMenu<AbstractTable<any>>;


   constructor(
      { id, isCashable, rows, cols, selection }: TableArgs<D>
   ) {
      this.id = id;
      this.isCashable = isCashable === undefined || isCashable;
      this.dom = new Dom(this, rows.maxVisibleCount);
      this.selection = new Selection(this, selection || stubSelection);
      this.cols = new Cols(this, cols);
      this.rows = new Rows(this, rows.maxVisibleCount);
      this.dragger = new Dragger(this);
      this.sizer = new Sizer(this);
      this.viewport = new Viewport(this.rows.maxVisibleCount);
      this.source = rows.source;
   }
}

